import React, { useEffect, useState, useCallback } from 'react';
import { Fingerprint } from 'lucide-react';
import { isNative } from '../lib/native/platform';
import { isBiometricAvailable, verifyBiometric } from '../lib/native/biometric';
import { prefGet } from '../lib/native/preferences';

// Optional biometric app-unlock overlay. NATIVE-ONLY: renders null on web, so
// ordinary browser users never see or are affected by it. This is a visual gate
// on TOP of the existing JWT session — it never bypasses server-side auth/RBAC
// (all API calls still require the valid token regardless of this overlay).
export const BiometricGate = () => {
  const [locked, setLocked] = useState(false);
  const [checking, setChecking] = useState(false);

  const maybeLock = useCallback(async () => {
    if (!isNative()) return;
    const enabled = (await prefGet('rvx_biometric_enabled')) === 'true';
    const hasSession = !!localStorage.getItem('accessToken');
    if (enabled && hasSession && (await isBiometricAvailable())) {
      setLocked(true);
    }
  }, []);

  const unlock = useCallback(async () => {
    setChecking(true);
    const ok = await verifyBiometric('Unlock ReasonVisionX');
    setChecking(false);
    if (ok) setLocked(false);
  }, []);

  useEffect(() => { maybeLock(); }, [maybeLock]);

  useEffect(() => {
    if (!isNative()) return;
    const onVis = () => { if (document.visibilityState === 'visible') maybeLock(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [maybeLock]);

  // Auto-prompt the OS dialog as soon as we lock.
  useEffect(() => { if (locked) unlock(); }, [locked, unlock]);

  if (!isNative() || !locked) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-slate-900 flex flex-col items-center justify-center text-white px-6"
      data-testid="biometric-lock"
    >
      <Fingerprint className="w-16 h-16 mb-5 text-blue-400" />
      <p className="text-lg font-semibold mb-1">ReasonVisionX is locked</p>
      <p className="text-sm text-slate-300 mb-8 text-center">Verify your identity to continue</p>
      <button
        onClick={unlock}
        disabled={checking}
        className="px-8 py-3 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 rounded-xl font-medium"
        data-testid="biometric-unlock-btn"
      >
        {checking ? 'Verifying…' : 'Unlock'}
      </button>
    </div>
  );
};

export default BiometricGate;
